import { Alarm, AlarmSeverity, DeliveryMediums, DefaultSettings, AlarmLabel } from "model";
import { DeviceType } from "model/Device";
import { toPairs, fromPairs, chain } from 'lodash';

export type AlarmSeverityValues = 'critical' | 'warning' | 'info';

type SystemModeValues = 'home' | 'away' | 'sleep';

interface DefaultSettingsData {
  enabled: boolean;
  supported: boolean;
  systemMode: SystemModeValues;
}

interface DefaultSettingsResponse {
  defaultSettings: DefaultSettingsData[];
  supported: boolean;
}

export interface DeliveryMediumsResponse {
  call: DefaultSettingsResponse;
  email: DefaultSettingsResponse;
  push: DefaultSettingsResponse;
  sms: DefaultSettingsResponse;
  userConfigurable: boolean;
}

export interface AlarmResponse {
  id: number;
  deliveryMedium: DeliveryMediumsResponse;
  isInternal: boolean;
  active: boolean;
  description: string;
  displayName: string;
  isShutoff: boolean;
  name: string;
  severity: AlarmSeverityValues;
  tags: string[];
  triggersAlarm?: { id: number };
  triggeredBy?: Array<{ id: number }>;
}

export interface AlarmsResponse {
  items: AlarmResponse[];
}

export const toSeverity = (severity: AlarmSeverityValues): AlarmSeverity => {
  switch(severity) {
    case 'critical':
      return AlarmSeverity.CRITICAL;
    case 'warning':
      return AlarmSeverity.WARNING;
    case 'info':
      return AlarmSeverity.INFO;
  }
}

export const fromSeverity = (severity: AlarmSeverity): AlarmSeverityValues => {
  switch(severity) {
    case AlarmSeverity.CRITICAL:
      return 'critical';
    case AlarmSeverity.WARNING:
      return 'warning';
    case AlarmSeverity.INFO:
      return 'info';
  }
}

export const toDeliveryMediums = (data: DeliveryMediumsResponse): DeliveryMediums => {
  const dataPairs = toPairs(data).filter(dp => dp[0] !== 'userConfigurable');
  const newPairs = dataPairs.map(([medium, settings]: [string, DefaultSettingsResponse]) => {
    return [ medium, {
      supported: settings.supported,
      default: {
        away: {
          enabled: settings.defaultSettings.find(def => def.systemMode === 'away')?.enabled || false,
          supported: settings.defaultSettings.find(def => def.systemMode === 'away')?.supported || false,
        },
        home: {
          enabled: settings.defaultSettings.find(def => def.systemMode === 'home')?.enabled || false,
          supported: settings.defaultSettings.find(def => def.systemMode === 'home')?.supported || false,
        },
        sleep: {
          enabled: settings.defaultSettings.find(def => def.systemMode === 'sleep')?.enabled || false,
          supported: settings.defaultSettings.find(def => def.systemMode === 'sleep')?.supported || false,
        },
      }
    }]
  })

  type Defaults = { sms: DefaultSettings, push: DefaultSettings, call: DefaultSettings, email: DefaultSettings };
  const defaults: Defaults = fromPairs(newPairs) as Defaults;

  return {
    userConfigurable: data.userConfigurable,
    ...defaults
  }
};

export const toAlarm = (data: AlarmResponse): Alarm => ({
  id: data.id,
  deliveryMediums: toDeliveryMediums(data.deliveryMedium),
  isInternal: data.isInternal,
  active: data.active,
  description: data.description,
  displayName: data.displayName,
  isShutoff: data.isShutoff,
  name: data.name,
  severity: toSeverity(data.severity),
  deviceType: data.tags.includes('flo_device_v2') ? DeviceType.SHUTOFF : DeviceType.DETECTOR,
  triggersAlarm: data.triggersAlarm?.id,
  triggeredBy: (data.triggeredBy?.length || 0) > 0 ? (data.triggeredBy ? data.triggeredBy[0] : { id: undefined }).id : undefined,
})

export const toAlarmLabelList = (data: AlarmsResponse): AlarmLabel[] => {
  const initValue: { [key: string]: AlarmLabel } = {};
  const grouped = data.items
    .filter(a => !a.isInternal)
    .reduce((prev, curr) => ({
      ...prev,
      [curr.name]: {
        name: curr.name,
        displayName: curr.displayName,
        ids: [
          ...(prev[curr.name] ? prev[curr.name].ids : []),
          curr.id
        ]
      }
    }), initValue);

  return chain(grouped)
    .values()
    .orderBy(l => l.displayName)
    .value();
}